import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import {
    Box,
    Typography,
    Button,
    Divider,
    Modal,
    Backdrop,
    Fade,
    CircularProgress
} from '@material-ui/core';

import client from '../../../utils/client';

import MultipleImageUploader from '../../common/MultipleImageUploader';
import { resizeFile } from '../../../utils/imageManager';

const useStyles = makeStyles((theme) => ({
    userImageUploader: {
        textAlign: 'center',
        padding: theme.spacing(3),
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(2),
        },
    },
    heading: {
        fontSize: '1.8rem',
        fontWeight: 600,
        color: theme.palette.text.primary,
        marginBottom: theme.spacing(3),
    },
    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    modalContent: {
        backgroundColor: theme.palette.background.paper,
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(4),
        [theme.breakpoints.down('sm')]: {
            padding: '0 1rem',
        },
        textAlign: 'center',
        outline: 'none',
        minWidth: '300px',
    },
    modalMessage: {
        fontSize: '1.6rem',
        color: theme.palette.text.primary,
        marginBottom: theme.spacing(2),
    },
    buttonContainer: {
        display: 'flex',
        gap: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            gap: theme.spacing(1),
        },
        justifyContent: 'center',
        marginTop: theme.spacing(3),
    },
    uploadButton: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
        '&:disabled': {
            backgroundColor: theme.palette.grey[400],
            color: theme.palette.grey[600],
        },
        [theme.breakpoints.down('sm')]: {
            fontSize: '1.4rem',
            padding: '0.6rem 1.2rem',
        },
    },
    cancelButton: {
        backgroundColor: theme.palette.error.main,
        color: theme.palette.error.contrastText,
        '&:hover': {
            backgroundColor: theme.palette.error.dark,
        },
        [theme.breakpoints.down('sm')]: {
            fontSize: '1.4rem',
            padding: '0.6rem 1.2rem',
        },
    },
    divider: {
        margin: theme.spacing(3, 0),
    },
}));

const UserImageUploader = (props) => {
    const classes = useStyles();
    const [selectedFiles, setFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [count, setCount] = useState(null);

    const onImagesSelected = async (selectedFiles) => {
        let resizedFiles = [];
        let maxSize = 1024;
        for (var file of selectedFiles) {
            let resizedFile = await resizeFile(file, maxSize, file.name);
            resizedFiles.push(resizedFile);
        }

        setFiles(resizedFiles);
    }

    const uploadImages = async files => {
        if (!files || files.length === 0) return;

        let failed = [];
        let count = 1;

        for (var file of files) {
            let fd = new FormData();
            fd.append('photo', file, file.name);
            setCount(count);
            try {
                await client.post('user/images', fd, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });
            } catch (error) {
                failed.push(file.name);
            }
            count++;
        }

        if (failed.length > 0) {
            console.log("Faild to upload some files");
            console.log(failed);
        }
    }

    const onUpload = async () => {
        setUploading(true);
        await uploadImages(selectedFiles);
        setUploading(false);
        props.onUploaded();
    }

    return (
        <Box className={classes.userImageUploader}>
            <Typography variant="h4" className={classes.heading}>
                画像アップロードする<br />（{props.maxNum}つまで）
            </Typography>

            <MultipleImageUploader onImagesSelected={onImagesSelected} maxNum={props.maxNum} />

            <Modal
                open={uploading}
                className={classes.modal}
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
            >
                <Fade in={uploading}>
                    <Box className={classes.modalContent}>
                        <CircularProgress style={{ marginBottom: '1rem' }} />
                        <Typography className={classes.modalMessage}>
                            {count}枚目の画像をアップロード中です…
                        </Typography>
                    </Box>
                </Fade>
            </Modal>

            <Divider className={classes.divider} />

            <Box className={classes.buttonContainer}>
                <Button
                    type="button"
                    onClick={onUpload}
                    className={classes.uploadButton}
                    variant="contained"
                    disabled={selectedFiles.length === 0 || uploading}
                >
                    アップロード
                </Button>
                <Button
                    className={classes.cancelButton}
                    type="button"
                    onClick={props.onCancel}
                    variant="contained"
                >
                    キャンセル
                </Button>
            </Box>
        </Box>
    )
}

UserImageUploader.propTypes = {
    onUploaded: PropTypes.func,
    onCancel: PropTypes.func,
    maxNum: PropTypes.number
}

export default UserImageUploader;
